<template>
  <div class="container-fluid p-0 mt-5">
    <div class="gl-line-height-normal mt-3">
      <div class="gl-display-flex gl-align-items-center mb-3 gl-justify-content-end">
        <gl-button
          category="primary"
          variant="default"
          size="medium"
          :selected="false"
          :loading="false"
          :disabled="false"
          :block="false"
          class=""
          @click="isGenerateWalletStatementModalOpen = true"
        >
          Generate wallet statement
        </gl-button>
        <gl-dropdown
          text="Add transaction"
          split=""
          category="primary"
          variant="confirm"
          size="medium"
          :block="false"
          :disabled="false"
          class="ml-3"
        >
          <gl-dropdown-item @click="goToCreditTransaction"
            >Credit transaction</gl-dropdown-item
          >
          <gl-dropdown-item @click="goToDebitTransaction">Debit transaction</gl-dropdown-item>
        </gl-dropdown>
      </div>
      <div class="p-3 gl-bg-gray-50 gl-border gl-border-gray-100">
        <gl-filtered-search
          placeholder="Search"
          v-model="filter"
          @keyup.enter="retrieveCompanyWalletTransactions"
          @submit="retrieveCompanyWalletTransactions"
          @clear="clearFilter"
          @clearHistory="clearFilter"
        ></gl-filtered-search>
      </div>
      <br />

      <gl-table
        class="mt-3"
        :items="walletTransactions"
        :filter="filter"
        :filter-included-fields="[]"
        :fields="fields"
        fixed
        stacked="md"
        :foot-clone="false"
        :selectable="true"
        selected-variant="primary"
        :caption="null"
        :show-empty="true"
        @row-selected="onRowSelected"
      >
        <template #cell(type)="data">
          <span v-if="data?.value" class="text-capitalize">
            <img
              v-if="['Outflow', 'outflow'].includes(data?.value)"
              src="@/assets/outflow.svg"
              alt="Outflow"
            />
            <img
              v-if="['Inflow', 'inflow'].includes(data?.value)"
              src="@/assets/inflow.svg"
              alt="Inflow"
            />
            {{ data?.value }}
          </span>
          <span v-else>N/A</span>
        </template>
        <template #cell(accountName)="data">
          <span v-if="data?.value" class="text-capitalize">
            {{ data?.value }}
          </span>
          <span v-else>N/A</span>
        </template>
        <template #cell(amount)="data">
          <span>{{ currency | formatCurrency }} {{ data?.value | formatNumber }}</span>
        </template>
        <template #cell(narration)="data">
          <span v-if="data?.value" class="text-capitalize" v-gl-tooltip :title="data?.value">
            {{ data?.value.length > 12 ? `${data?.value?.slice(0, 24)}....` : data?.value }}
          </span>
          <span v-else>N/A</span>
        </template>
        <template #cell(balance)="data">
          <span>{{ currency | formatCurrency }} {{ data?.value | formatNumber }}</span>
        </template>
        <template #cell(approvedAt)="data">
          <span>{{ data?.value | humanReadableDate }}</span>
        </template>
        <template #cell(transactedAt)="data">
          <span>{{ data?.value | humanReadableDate }}</span>
        </template>
      </gl-table>
      <div class="gl-display-flex gl-align-items-center gl-justify-content-space-between mt-3">
        <span>{{ walletTransactions?.length }} transactions</span>
      </div>
    </div>

    <gl-modal
      v-model="isGenerateWalletStatementModalOpen"
      modalId="generate-wallet-statement"
      title="Generate wallet statement"
      :actionPrimary="{
        text: 'Generate wallet statement',
        attributes: {
          variant: 'confirm',
          disabled: !datesAdded,
        },
      }"
      :actionCancel="{ text: 'Cancel' }"
      :visible="false"
      :noFocusOnShow="false"
      size="sm"
      :scrollable="false"
      no-fade=""
      @primary="generateWalletStatement"
    >
      <div>
        <p class="mb-4 mt-2">
          Select the start and end dates you want to generate a statement for, as well as your
          preferred format.
        </p>
      </div>
      <gl-form-group id="input-group-3" label="Start date" label-for="start-date" class="">
        <gl-datepicker
          class="input-item w-full datepicker-item"
          style="width: 100% !important"
          size="md"
          id="start-date"
          placeholder="DD/MM/YY"
          :showClearButton="true"
          @input="updateStartDate"
        />
      </gl-form-group>

      <gl-form-group id="input-group-3" label="End date" label-for="end-date" class="mt-4">
        <gl-datepicker
          class="input-item w-full datepicker-item"
          style="width: 100% !important"
          size="md"
          id="end-date"
          placeholder="DD/MM/YY"
          :showClearButton="true"
          @input="updateEndDate"
        />
      </gl-form-group>

      <p class="mb-2 mt-4">File format</p>
      <div>
        <gl-form-radio v-model="selected" value="Pdf">PDF </gl-form-radio>
        <gl-form-radio v-model="selected" value="Excel">Excel </gl-form-radio>
      </div>
    </gl-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { dateHelper } from '@/utils';
const { DATETIMEFORMAT } = dateHelper;

import {
  GlFilteredSearch,
  GlDropdown,
  GlDropdownItem,
  GlButton,
  GlTable,
  GlDatepicker,
  GlModal,
  GlFormGroup,
  GlFormRadio,
  GlTooltipDirective,
} from '@gitlab/ui';
export default {
  name: 'WalletTransactionHistory',
  components: {
    GlButton,
    GlFilteredSearch,
    GlDropdownItem,
    GlDropdown,
    GlTable,
    GlDatepicker,
    GlModal,
    GlFormGroup,
    GlFormRadio,
  },
  directives: {
    GlTooltipDirective,
  },
  props: {
    walletTransactions: {
      type: Array,
      required: true,
    },
    walletId: {
      type: [Number, String],
    },
    currency: {
      type: String,
    },
  },
  data: () => ({
    isGenerateWalletStatementModalOpen: false,
    selected: 'Pdf',
    payload: {
      StartAt: '',
      EndAt: '',
      Format: '',
      Search: '',
    },
    filter: [],
    fields: [
      {
        key: 'type',
        label: 'Transaction type',
      },
      {
        key: 'accountName',
        label: 'Sender/Recipient',
      },
      {
        key: 'amount',
        label: 'Amount',
      },
      {
        key: 'narration',
        label: 'Description',
      },
      {
        key: 'balance',
        label: 'Balance',
      },
      {
        key: 'approvedAt',
        label: 'Approved date',
      },
      {
        key: 'transactedAt',
        label: 'Value date'
      },
      {
        key: 'approvedAt',
        label: 'Approved date',
      },
      {
        key: 'transactedAt',
        label: 'Value date',
      }
    ],
  }),
  computed: {
    datesAdded() {
      return !!this.payload.StartAt && !!this.payload.EndAt;
    },
    isCustomerWalletRoute() {
      return this.$route.path.includes('/all-customers');
    },
    walletOwnerId() {
      return (
        this.$route.params.companyId || this.$route.params.customerId || this.$route.params.id
      );
    },
  },
  methods: {
    toggleLoading(isLoading) {
      this.$store.dispatch('loading/toggleLoading', isLoading);
    },
    goToCreditTransaction() {
      this.$router.push({
        name: this.isCustomerWalletRoute
          ? 'CustomerAddCreditTransaction'
          : 'AddCreditTransaction',
        params: { id: this.walletOwnerId, walletId: this.walletId },
      });
    },
    goToDebitTransaction() {
      this.$router.push({
        name: this.isCustomerWalletRoute
          ? 'CustomerAddDebitTransaction'
          : 'AddDebitTransaction',
        params: { id: this.walletOwnerId, walletId: this.walletId },
      });
    },
    clearFilter() {
      this.filter = [];
      this.$emit('search-transactions', '');
    },
    onRowSelected() {},
    async retrieveCompanyWalletTransactions() {
      this.$emit(
        'search-transactions',
        this.filter
          .map((f) => f?.value?.data)
          .filter(Boolean)
          .join(' ')
      );
    },
    updateStartDate(value) {
      this.payload['StartAt'] = this.formatTimeToDateTime(
        dayjs(value).startOf('day').utc().format(DATETIMEFORMAT)
      );
    },
    updateEndDate(value) {
      this.payload['EndAt'] = this.formatTimeToDateTime(
        dayjs(value).endOf('day').utc().format(DATETIMEFORMAT)
      );
    },
    generateWalletStatement() {
      this.payload['Search'] = this.filter
        .map((f) => f?.value?.data)
        .filter(Boolean)
        .join(' ');
      this.payload['Format'] = this.selected;

      this.$emit('generate-statement', this.payload);
    },

    formatTimeToDateTime(timePeriod) {
      let splitted = timePeriod.split(' ');
      splitted.splice(1, 0, 'T');
      splitted.push('Z');
      return splitted.join('');
    },
  },
};
</script>

<style></style>
